import apiClient from '../api-client'

// Re-export all types from dedicated types module
export type {
  AuditJob,
  AuditReport,
  AuditVersion,
  UnitEconomics,
  AttributeIntelligence,
  ChannelReadiness,
  Positioning,
  ProductSummary,
  BrandAnalysis,
  ProductResearch,
  ProductListResponse,
  RemakeJob,
  RemakeResult,
  RemakeVariant,
  VariantEdits,
  UserEdits,
  ComplianceRules,
  FieldRules,
  ForbiddenPattern,
  StyleRule,
  RemakeVersion,
  ImageSlot,
  SlotGeneration,
  RerunResult,
  AuditShareStatus,
  AuditShareResult,
  PublicAuditData,
} from './amazon-listing.types'

// Re-export optimization types from dedicated module
export type {
  OptimizationVariant,
  BackendKeywords,
  KeywordCoverageItem,
  KeywordReport,
  OptimizationResult,
} from './amazon-listing-optimizer'
export { startOptimization, getOptimizationResult } from './amazon-listing-optimizer'

import type {
  AuditJob,
  AuditReport,
  AuditShareResult,
  AuditShareStatus,
  AuditVersion,
  ComplianceRules,
  ImageSlot,
  ProductListResponse,
  PublicAuditData,
  RemakeJob,
  RemakeVersion,
  RerunResult,
  UserEdits,
} from './amazon-listing.types'

export interface AuditStartResult {
  job_id: string
  audit_product_id?: string
}

export async function startAudit(data: {
  asin: string
  tld: string
  report_language: string
  mode?: 'small' | 'standard' | 'custom'
  dev_mode?: boolean
}): Promise<AuditStartResult> {
  const resp = await apiClient.post('/api/v2/amazon/audit', data)
  return resp.data
}

export interface BatchAuditResult {
  created: AuditStartResult[]
  errors: { asin: string; error: string }[]
  total_created: number
  total_errors: number
}

export async function startBatchAudit(data: {
  asins: string[]
  tld: string
  report_language: string
  mode?: 'small' | 'standard' | 'custom'
  dev_mode?: boolean
}): Promise<BatchAuditResult> {
  const resp = await apiClient.post('/api/v2/amazon/audit/batch', data)
  return resp.data
}

export async function getCeleryStatus(): Promise<{ available: boolean }> {
  const resp = await apiClient.get('/api/v2/amazon/celery-status')
  return resp.data
}

export async function getAuditStatus(jobId: string): Promise<AuditJob> {
  const resp = await apiClient.get(`/api/v2/amazon/audit/${jobId}/status`)
  return resp.data
}

export async function getAuditReport(jobId: string): Promise<AuditReport> {
  const resp = await apiClient.get(`/api/v2/amazon/audit/${jobId}/report`)
  return resp.data
}

export interface AuditRestartResult {
  job_id: string
  audit_product_id: string | null
  version: number
  status: string
  credits_charged: number
  restarted_from: string
}

export async function restartAudit(jobId: string): Promise<AuditRestartResult> {
  const resp = await apiClient.post(`/api/v2/amazon/audit/${jobId}/restart`)
  return resp.data
}

export async function startManualAudit(data: {
  tld: string
  title: string
  bullets: string[]
  description: string
  brand: string
  image_asset_ids: string[]
  report_language?: string
  mode?: 'small' | 'standard' | 'custom'
  audit_scope?: 'audit_only' | 'audit_full'
  gtin?: string
  sku?: string
  price?: string
  category?: string
  goki_product_id?: string
}): Promise<AuditStartResult> {
  const resp = await apiClient.post('/api/v2/amazon/audit/manual', data)
  return resp.data
}

export async function startChannelAudit(data: {
  product_id: string
  channel_id: string
  tld: string
  report_language?: string
  dev_mode?: boolean
}) {
  const resp = await apiClient.post('/api/v2/amazon/audit/channel', data)
  return resp.data
}

export async function getProductPrefill(productId: string) {
  const resp = await apiClient.get(`/api/v2/amazon/product-prefill/${productId}`)
  return resp.data as {
    product: {
      id: string; name: string; sku?: string; gtin?: string
      brand?: string; price?: string; category?: string; image_url?: string
    }
    channels: { id: string; name: string; channel_name: string }[]
  }
}

export async function getChannelContent(productId: string, channelId: string) {
  const resp = await apiClient.get(
    `/api/v2/amazon/product-prefill/${productId}/channel/${channelId}`,
  )
  return resp.data as {
    title: string
    bullets: string[]
    description: string
    images: { url: string; slot: number }[]
  }
}

export async function processAuditQueue(): Promise<{ status: string; job_id?: string }> {
  const resp = await apiClient.post('/api/v2/amazon/audit/process-queue')
  return resp.data
}

export async function listProducts(params: {
  limit?: number
  offset?: number
  search?: string
}): Promise<ProductListResponse> {
  const resp = await apiClient.get('/api/v2/amazon/products', { params })
  return resp.data
}

export async function deleteAudit(jobId: string): Promise<void> {
  await apiClient.delete(`/api/v2/amazon/audit/${jobId}`)
}

export async function deleteAuditProduct(productId: string): Promise<void> {
  await apiClient.delete(`/api/v2/amazon/products/${productId}`)
}

export async function reauditProduct(jobId: string, data?: { report_language?: string; dev_mode?: boolean }): Promise<AuditStartResult> {
  const resp = await apiClient.post(`/api/v2/amazon/audit/${jobId}/reaudit`, data ?? {})
  return resp.data
}

export async function getAuditVersions(productId: string) {
  const resp = await apiClient.get(`/api/v2/amazon/products/${productId}/versions`)
  return resp.data.versions as AuditVersion[]
}

export async function startRemake(jobId: string): Promise<{ remake_id: string }> {
  const resp = await apiClient.post(`/api/v2/amazon/audit/${jobId}/remake`)
  return resp.data
}

export async function cancelRemake(jobId: string, remakeId: string): Promise<void> {
  await apiClient.post(`/api/v2/amazon/audit/${jobId}/remake/${remakeId}/cancel`)
}

export async function getRemakeStatus(jobId: string): Promise<RemakeJob> {
  const resp = await apiClient.get(`/api/v2/amazon/audit/${jobId}/remake/status`)
  return resp.data
}

export async function getLatestRemake(jobId: string): Promise<RemakeJob> {
  const resp = await apiClient.get(`/api/v2/amazon/audit/${jobId}/remake/latest`)
  return resp.data
}

export async function getRemakeVersions(jobId: string): Promise<RemakeVersion[]> {
  const resp = await apiClient.get(`/api/v2/amazon/audit/${jobId}/remake/versions`)
  return resp.data.versions ?? []
}

export async function getRemakeById(remakeId: string): Promise<RemakeJob> {
  const resp = await apiClient.get(`/api/v2/amazon/remake/${remakeId}`)
  return resp.data
}

export async function saveRemakeEdits(jobId: string, remakeId: string, edits: UserEdits): Promise<void> {
  await apiClient.put(`/api/v2/amazon/audit/${jobId}/remake/${remakeId}/edits`, edits)
}

export async function applyRemakeImageToSlot(
  remakeId: string,
  generationId: string,
  body: { product_id: string; channel_id: string; slot: number },
): Promise<{ asset_id: string }> {
  const resp = await apiClient.post(
    `/api/v2/amazon/remake/${remakeId}/generations/${generationId}/apply-to-slot`,
    body,
  )
  return resp.data
}

export async function getComplianceRules(marketplace: string): Promise<ComplianceRules> {
  const resp = await apiClient.get(`/api/v2/amazon/rules/${marketplace}`)
  return resp.data
}

export async function getImageSlots(remakeId: string): Promise<ImageSlot[]> {
  const resp = await apiClient.get(`/api/v2/amazon/remake/${remakeId}/slots`)
  return resp.data.slots
}

export async function generateSlot(slotId: string): Promise<{ slot_id: string; status: string }> {
  const resp = await apiClient.post(`/api/v2/amazon/slots/${slotId}/generate`)
  return resp.data
}

export async function rerunSlot(slotId: string, opts?: {
  modified_prompt?: string; overlay_text?: string; info_overlay_text?: string
  font_family?: string; edit_instructions?: string; resolution?: string
  aspect_ratio?: string; reference_image_urls?: string[]; generation_count?: number
}): Promise<RerunResult> {
  const resp = await apiClient.post(`/api/v2/amazon/slots/${slotId}/rerun`, opts ?? {})
  return resp.data
}

export async function selectSlotGeneration(slotId: string, generationId: string): Promise<void> {
  await apiClient.post(`/api/v2/amazon/slots/${slotId}/select`, { generation_id: generationId })
}

export async function downloadSlotGeneration(slotId: string, generationId: string): Promise<Blob> {
  const resp = await apiClient.get(
    `/api/v2/amazon/slots/${slotId}/generations/${generationId}/download`,
    { responseType: 'blob' },
  )
  return resp.data
}

export function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export async function updateProductResearch(
  jobId: string,
  productResearch: Record<string, unknown>,
): Promise<void> {
  await apiClient.patch(`/api/v2/amazon/audit/${jobId}/product-research`, {
    product_research: productResearch,
  })
}

export async function markAuditViewed(jobId: string): Promise<void> {
  await apiClient.post(`/api/v2/amazon/audit/${jobId}/viewed`)
}

export async function downloadAuditReportPdf(jobId: string, language: string): Promise<Blob> {
  const resp = await apiClient.get(`/api/v2/amazon/audit/${jobId}/report/pdf`, {
    params: { lang: language },
    responseType: 'blob',
  })
  return resp.data
}

export async function getShareStatus(jobId: string): Promise<AuditShareStatus> {
  const resp = await apiClient.get(`/api/v2/amazon/audit/${jobId}/share`)
  return resp.data
}

export async function createShare(
  jobId: string,
  options?: { password?: string; include_remake?: boolean }
): Promise<AuditShareResult> {
  const resp = await apiClient.post(`/api/v2/amazon/audit/${jobId}/share`, {
    password: options?.password || undefined,
    include_remake: options?.include_remake ?? false,
  })
  return resp.data
}

export async function deactivateShare(jobId: string): Promise<void> {
  await apiClient.delete(`/api/v2/amazon/audit/${jobId}/share`)
}

export async function fetchPublicAudit(
  shareToken: string,
  password?: string
): Promise<PublicAuditData> {
  const resp = await fetch(`/api/v2/public/audit/${shareToken}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password: password || undefined }),
  })
  if (!resp.ok) {
    throw Object.assign(new Error(resp.statusText), {
      response: { status: resp.status },
    })
  }
  return resp.json()
}

export interface CompetitorResult {
  asin: string
  title: string
  price: string | null
  rating: number | null
  reviews_count: number | null
}

export async function researchCompetitors(params: {
  keyword: string
  tld: string
  source_asin?: string
}): Promise<CompetitorResult[]> {
  const resp = await apiClient.post('/api/v2/amazon/competitor-research', params)
  return resp.data.competitors
}
